import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import OneSignalService from '@/services/OneSignalService'

export interface NotificationPreferences {
  training_reminders: boolean
  class_requests: boolean
  direct_messages: boolean
  roll_review: boolean
}

interface UseNotificationPreferencesReturn {
  preferences: NotificationPreferences
  isLoading: boolean
  error: string | null
  updatePreference: (key: keyof NotificationPreferences, value: boolean) => Promise<void>
}

// Default preferences: all enabled
const DEFAULT_PREFERENCES: NotificationPreferences = {
  training_reminders: true,
  class_requests: true,
  direct_messages: true,
}

export const useNotificationPreferences = (): UseNotificationPreferencesReturn => {
  const { user } = useAuth()
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch preferences on mount or when user changes
  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false)
      return
    }

    const fetchPreferences = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const { data, error: fetchError } = await supabase
          .from('user_notification_preferences')
          .select('training_reminders, class_requests, direct_messages, roll_review')
          .eq('user_id', user.id)
          .maybeSingle()

        if (fetchError) {
          console.error('[useNotificationPreferences] Error fetching preferences:', fetchError)
          setError(fetchError.message)
          setPreferences(DEFAULT_PREFERENCES)
          return
        }

        if (data) {
          setPreferences(data as NotificationPreferences)
        } else {
          // First time: no row yet, use defaults
          setPreferences(DEFAULT_PREFERENCES)
        }
      } catch (err) {
        console.error('[useNotificationPreferences] Fetch error:', err)
        setError(err instanceof Error ? err.message : 'Failed to load preferences')
        setPreferences(DEFAULT_PREFERENCES)
      } finally {
        setIsLoading(false)
      }
    }

    fetchPreferences()
  }, [user?.id])

  // Update a single preference
  const updatePreference = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!user?.id) return

    const updatedPrefs = { ...preferences, [key]: value }
    setPreferences(updatedPrefs)

    try {
      // Upsert into database
      const { error: upsertError } = await supabase
        .from('user_notification_preferences')
        .upsert(
          {
            user_id: user.id,
            [key]: value,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        )

      if (upsertError) {
        console.error('[useNotificationPreferences] Error updating preference:', upsertError)
        setError(upsertError.message)
        // Revert on error
        setPreferences((prev) => ({ ...prev, [key]: !value }))
        return
      }

      // Sync with OneSignal — tag is '1' for true, '0' for false
      const oneSignalTagValue = value ? '1' : '0'
      await OneSignalService.setTag(key, oneSignalTagValue as '1' | '0')

      setError(null)
    } catch (err) {
      console.error('[useNotificationPreferences] Update error:', err)
      setError(err instanceof Error ? err.message : 'Failed to update preference')
      // Revert on error
      setPreferences((prev) => ({ ...prev, [key]: !value }))
    }
  }

  return {
    preferences,
    isLoading,
    error,
    updatePreference,
  }
}
